import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DocumentTextIcon,
  PlusIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  TagIcon,
  ArrowDownTrayIcon,
  DocumentArrowUpIcon,
  DocumentArrowDownIcon,
  FolderIcon,
  LinkIcon,
  EyeIcon,
  ChartBarIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import {
  collection,
  query,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  orderBy,
  increment,
  getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { firestore, storage } from '../../../database/firebase';
import { useAuth } from '../../../context/AuthContext';
import { useRole } from '../../../hooks/useRole';
import { useToast } from '../../../context/ToastContext';
import { DeleteConfirmation } from '../../ui/DeleteConfirmation';

const Templates = () => {
  const { currentUser } = useAuth();
  const { userRole } = useRole();
  const toast = useToast();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [newTemplate, setNewTemplate] = useState({
    title: '',
    description: '',
    category: 'business',
    fileUrl: '',
    fileType: '',
    fileName: '',
    link: '',
    tags: [],
    documents: [],
    steps: [],
    strategicImplications: '',
    conclusion: '',
  });

  const isCoach = userRole === 'coach' || userRole === 'admin' || userRole === 'superAdmin';

  useEffect(() => {
    setLoading(true);
    let unsubscribe = null;

    // Helper function to process snapshot data
    const processSnapshot = snapshot => {
      const templatesData = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Handle Firestore timestamp conversion
          createdAt: data.createdAt?.toDate
            ? data.createdAt.toDate()
            : data.createdAt?.seconds
              ? new Date(data.createdAt.seconds * 1000)
              : data.createdAt || new Date(),
          updatedAt: data.updatedAt?.toDate
            ? data.updatedAt.toDate()
            : data.updatedAt?.seconds
              ? new Date(data.updatedAt.seconds * 1000)
              : data.updatedAt || new Date(),
        };
      });

      // Sort manually by createdAt (descending)
      templatesData.sort((a, b) => {
        const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
        const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
        return bDate.getTime() - aDate.getTime();
      });

      setTemplates(templatesData);
      setLoading(false);
    };

    // Try query with orderBy first
    const tryQueryWithOrderBy = () => {
      const templatesQuery = query(
        collection(firestore, 'templates'),
        orderBy('createdAt', 'desc')
      );

      unsubscribe = onSnapshot(templatesQuery, processSnapshot, error => {
        console.error('Error listening to templates:', error);

        // If orderBy fails due to missing index, try without orderBy
        if (error.code === 'failed-precondition' || error.message?.includes('index')) {
          console.info('Index not yet created, using fallback query');
          tryQueryWithoutOrderBy();
        } else {
          toast.error('Failed to load templates', 'Error');
          setLoading(false);
        }
      });
    };

    // Fallback query without orderBy
    const tryQueryWithoutOrderBy = () => {
      if (unsubscribe) unsubscribe(); // Clean up previous listener

      const simpleQuery = query(collection(firestore, 'templates'));

      unsubscribe = onSnapshot(simpleQuery, processSnapshot, fallbackError => {
        console.error('Error in fallback query:', fallbackError);
        toast.error('Failed to load templates', 'Error');
        setLoading(false);
      });
    };

    // Start with orderBy query
    tryQueryWithOrderBy();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const categories = [
    { id: 'all', name: 'All Categories', color: 'gray' },
    { id: 'business', name: 'Business Plans', color: 'amber' },
    { id: 'financial', name: 'Financial', color: 'emerald' },
    { id: 'marketing', name: 'Marketing', color: 'purple' },
    { id: 'legal', name: 'Legal', color: 'blue' },
    { id: 'presentation', name: 'Presentations', color: 'pink' },
    { id: 'other', name: 'Other', color: 'gray' },
  ];

  const getCategoryColor = categoryId => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return 'gray';

    const colorMap = {
      amber: 'bg-amber-100 text-amber-800 border-amber-200',
      emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      purple: 'bg-purple-100 text-purple-800 border-purple-200',
      blue: 'bg-blue-100 text-blue-800 border-blue-200',
      pink: 'bg-pink-100 text-pink-800 border-pink-200',
      gray: 'bg-gray-100 text-gray-800 border-gray-200',
    };

    return colorMap[category.color] || colorMap.gray;
  };

  // Helper function to normalize step data (handle backward compatibility)
  const normalizeStep = step => {
    if (typeof step === 'string') {
      return { title: step, description: '' };
    }
    return {
      title: step?.title || '',
      description: step?.description || '',
    };
  };

  const getFileIcon = fileType => {
    if (!fileType) return DocumentTextIcon;
    const type = fileType.toLowerCase();
    if (type.includes('pdf')) return DocumentTextIcon;
    if (type.includes('word') || type.includes('doc')) return DocumentTextIcon;
    if (type.includes('excel') || type.includes('xls')) return DocumentTextIcon;
    if (type.includes('powerpoint') || type.includes('ppt')) return DocumentTextIcon;
    return DocumentTextIcon;
  };

  const handleDocumentUpload = e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Validate file types and sizes
    const validFiles = [];
    const maxSize = 50 * 1024 * 1024; // 50MB per file

    files.forEach(file => {
      // Allow common document types
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain',
        'text/csv',
      ];

      if (
        !allowedTypes.includes(file.type) &&
        !file.name.match(/\.(pdf|doc|docx|xls|xlsx|ppt|pptx|txt|csv)$/i)
      ) {
        toast.error(`File "${file.name}" is not a supported document type`, 'Error');
        return;
      }

      if (file.size > maxSize) {
        toast.error(`File "${file.name}" exceeds 50MB limit`, 'Error');
        return;
      }

      validFiles.push(file);
    });

    if (validFiles.length > 0) {
      setDocumentFiles(prev => [...prev, ...validFiles]);
      toast.success(`${validFiles.length} document(s) added`, 'Success');
    }
  };

  const removeDocumentFile = index => {
    setDocumentFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeDocument = async (templateId, docIndex) => {
    try {
      const template = templates.find(t => t.id === templateId);
      if (!template || !template.documents || !template.documents[docIndex]) return;

      const docToDelete = template.documents[docIndex];

      // Delete from storage
      if (docToDelete.url) {
        try {
          const docRef = ref(storage, docToDelete.url);
          await deleteObject(docRef);
        } catch (error) {
          console.warn('Failed to delete document from storage:', error);
        }
      }

      // Update template to remove document
      const updatedDocuments = template.documents.filter((_, i) => i !== docIndex);
      await updateDoc(doc(firestore, 'templates', templateId), {
        documents: updatedDocuments,
        updatedAt: serverTimestamp(),
      });

      toast.success('Document removed successfully');
    } catch (error) {
      console.error('Error removing document:', error);
      toast.error('Failed to remove document', 'Error');
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!newTemplate.title || !newTemplate.description) {
      toast.warning('Please fill in all required fields', 'Validation Error');
      return;
    }

    if (
      !newTemplate.fileUrl &&
      !newTemplate.link &&
      (!newTemplate.documents || newTemplate.documents.length === 0) &&
      documentFiles.length === 0
    ) {
      toast.error(
        'Please upload a file, add documents, or provide an external link',
        'Validation Error'
      );
      return;
    }

    setSavingTemplate(true);
    try {
      // Fetch user profile to get firstname and lastname
      let authorName = currentUser.displayName || currentUser.email;
      let authorFirstName = '';
      let authorLastName = '';

      try {
        // Try to get coach profile first
        const coachDoc = await getDoc(doc(firestore, 'coaches', currentUser.uid));
        if (coachDoc.exists()) {
          const coachData = coachDoc.data();
          authorFirstName = coachData.firstname || '';
          authorLastName = coachData.lastname || '';
          if (authorFirstName || authorLastName) {
            authorName = `${authorFirstName} ${authorLastName}`.trim();
          }
        } else {
          // Try admin collection
          const adminDoc = await getDoc(doc(firestore, 'admin', currentUser.uid));
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            authorFirstName = adminData.firstname || '';
            authorLastName = adminData.lastname || '';
            if (authorFirstName || authorLastName) {
              authorName = `${authorFirstName} ${authorLastName}`.trim();
            }
          } else {
            // Try moderator collection
            const moderatorDoc = await getDoc(doc(firestore, 'moderator', currentUser.uid));
            if (moderatorDoc.exists()) {
              const moderatorData = moderatorDoc.data();
              authorFirstName = moderatorData.firstname || '';
              authorLastName = moderatorData.lastname || '';
              if (authorFirstName || authorLastName) {
                authorName = `${authorFirstName} ${authorLastName}`.trim();
              }
            }
          }
        }
      } catch (error) {
        console.warn('Failed to fetch user profile:', error);
        // Continue with email as fallback
      }

      let documents = [...(newTemplate.documents || [])];

      // Upload new documents if any files are selected
      if (documentFiles.length > 0) {
        setUploadingDocuments(true);
        try {
          const uploadPromises = documentFiles.map(async file => {
            const timestamp = Date.now();
            const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
            const documentRef = ref(storage, `templates/documents/${timestamp}_${safeName}`);
            await uploadBytes(documentRef, file);
            const downloadURL = await getDownloadURL(documentRef);
            return {
              name: file.name,
              url: downloadURL,
              size: file.size,
              type: file.type,
              uploadedAt: new Date().toISOString(),
            };
          });

          const uploadedDocs = await Promise.all(uploadPromises);
          documents = [...documents, ...uploadedDocs];
        } catch (error) {
          console.error('Error uploading documents:', error);
          toast.error('Failed to upload some documents', 'Error');
          setUploadingDocuments(false);
          setSavingTemplate(false);
          return;
        }
        setUploadingDocuments(false);
      }

      const templateData = {
        ...newTemplate,
        documents: documents,
        createdBy: currentUser.email,
        createdByName: authorName,
        createdByFirstName: authorFirstName,
        createdByLastName: authorLastName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        // Initialize analytics
        views: editingTemplate?.views || 0,
        clicks: editingTemplate?.clicks || 0,
        lastViewedAt: editingTemplate?.lastViewedAt || null,
      };

      // Remove storagePath from data (internal use only)
      delete templateData.storagePath;

      if (editingTemplate) {
        await updateDoc(doc(firestore, 'templates', editingTemplate.id), {
          ...templateData,
          updatedAt: serverTimestamp(),
        });
        toast.success('Template updated successfully!');
      } else {
        await addDoc(collection(firestore, 'templates'), templateData);
        toast.success('Template created successfully!');
      }

      setShowModal(false);
      setEditingTemplate(null);
      setNewTemplate({
        title: '',
        description: '',
        category: 'business',
        fileUrl: '',
        fileType: '',
        fileName: '',
        link: '',
        tags: [],
        documents: [],
        steps: [],
        strategicImplications: '',
        conclusion: '',
      });
      setDocumentFiles([]);
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template', 'Error');
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleDeleteClick = template => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      // Delete file from storage if exists
      if (templateToDelete.fileUrl) {
        try {
          const fileRef = ref(storage, templateToDelete.fileUrl);
          await deleteObject(fileRef);
        } catch (error) {
          console.warn('Failed to delete file:', error);
        }
      }

      // Delete documents from storage if they exist
      if (templateToDelete.documents && templateToDelete.documents.length > 0) {
        try {
          const deletePromises = templateToDelete.documents.map(async doc => {
            if (doc.url) {
              const docRef = ref(storage, doc.url);
              await deleteObject(docRef);
            }
          });
          await Promise.all(deletePromises);
        } catch (error) {
          console.warn('Failed to delete some documents:', error);
        }
      }

      await deleteDoc(doc(firestore, 'templates', templateToDelete.id));
      toast.success('Template deleted successfully!');
      setShowDeleteModal(false);
      setTemplateToDelete(null);
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template', 'Error');
    }
  };

  const handleEdit = template => {
    setEditingTemplate(template);
    // Normalize steps to ensure they're objects
    const normalizedSteps = (template.steps || []).map(step => normalizeStep(step));
    setNewTemplate({
      title: template.title || '',
      description: template.description || '',
      category: template.category || 'business',
      fileUrl: template.fileUrl || '',
      fileType: template.fileType || '',
      fileName: template.fileName || '',
      link: template.link || '',
      tags: template.tags || [],
      documents: template.documents || [],
      steps: normalizedSteps,
      strategicImplications: template.strategicImplications || '',
      conclusion: template.conclusion || '',
    });
    setDocumentFiles([]);
    setShowModal(true);
  };

  const handleCardClick = async template => {
    setSelectedTemplate(template);
    setShowDetailModal(true);

    // Track view analytics
    try {
      const templateRef = doc(firestore, 'templates', template.id);
      await updateDoc(templateRef, {
        views: increment(1),
        lastViewedAt: serverTimestamp(),
      });

      // Log detailed analytics event
      await addDoc(collection(firestore, 'templatesAnalytics'), {
        templateId: template.id,
        templateTitle: template.title,
        userId: currentUser?.uid || 'anonymous',
        userEmail: currentUser?.email || 'anonymous',
        userRole: userRole || 'unknown',
        action: 'view',
        timestamp: serverTimestamp(),
        metadata: {
          category: template.category,
          hasDocuments: !!(template.documents && template.documents.length > 0),
          hasFile: !!template.fileUrl,
          hasLink: !!template.link,
        },
      });
    } catch (error) {
      console.error('Error tracking analytics:', error);
    }
  };

  const addStep = () => {
    setNewTemplate({
      ...newTemplate,
      steps: [...(newTemplate.steps || []), { title: '', description: '' }],
    });
  };

  const updateStep = (index, field, value) => {
    const updatedSteps = [...(newTemplate.steps || [])];
    // Handle backward compatibility - convert string steps to objects
    if (typeof updatedSteps[index] === 'string') {
      updatedSteps[index] = { title: updatedSteps[index], description: '' };
    }
    updatedSteps[index] = {
      ...updatedSteps[index],
      [field]: value,
    };
    setNewTemplate({ ...newTemplate, steps: updatedSteps });
  };

  const removeStep = index => {
    const updatedSteps = newTemplate.steps.filter((_, i) => i !== index);
    setNewTemplate({ ...newTemplate, steps: updatedSteps });
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch =
      template.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl bg-white p-6 shadow-xl"
      >
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-200 border-t-amber-600"></div>
            <p className="font-medium text-gray-600">Loading templates...</p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl bg-white p-4 shadow-xl sm:p-6"
    >
      {/* Header */}
      <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
            <DocumentTextIcon className="h-7 w-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Templates</h2>
            <p className="text-sm text-gray-500">Reusable templates and document formats</p>
          </div>
        </div>
        {isCoach && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setEditingTemplate(null);
              setNewTemplate({
                title: '',
                description: '',
                category: 'business',
                fileUrl: '',
                fileType: '',
                fileName: '',
                link: '',
                tags: [],
                documents: [],
                steps: [],
                strategicImplications: '',
                conclusion: '',
              });
              setDocumentFiles([]);
              setShowModal(true);
            }}
            className="flex items-center gap-2 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:from-amber-600 hover:to-orange-700 hover:shadow-xl"
          >
            <PlusIcon className="h-5 w-5" />
            <span className="hidden sm:inline">Add Template</span>
          </motion.button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 pl-10 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={e => setSelectedCategory(e.target.value)}
          className="rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>

        {/* View Mode Toggle */}
        <div className="flex rounded-xl border border-gray-300 bg-white p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'grid'
                ? 'bg-amber-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <Squares2X2Icon className="h-4 w-4" />
            <span className="hidden sm:inline">Grid</span>
          </button>
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
              viewMode === 'table'
                ? 'bg-amber-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
            }`}
          >
            <TableCellsIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Table</span>
          </button>
        </div>
      </div>

      {/* Templates View */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {filteredTemplates.map((template, index) => {
              const FileIcon = getFileIcon(template.fileType);
              return (
                <motion.div
                  key={template.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9, y: -20 }}
                  transition={{
                    delay: index * 0.05,
                    layout: { duration: 0.3 },
                  }}
                  onClick={() => handleCardClick(template)}
                  className="group relative cursor-pointer overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-br from-white to-amber-50/30 shadow-sm transition-all hover:scale-[1.02] hover:border-amber-300 hover:shadow-lg"
                >
                  <div className="relative p-6">
                    <div className="mb-4 flex items-start justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                        <FileIcon className="h-6 w-6 text-white" />
                      </div>
                      {isCoach && (
                        <div className="flex items-center gap-1">
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleEdit(template);
                            }}
                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-amber-600"
                            aria-label="Edit template"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </motion.button>
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteClick(template);
                            }}
                            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-red-600"
                            aria-label="Delete template"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </motion.button>
                        </div>
                      )}
                    </div>

                    <h3 className="mb-2 text-xl font-bold text-gray-900 transition-colors group-hover:text-amber-600">
                      {template.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 text-sm leading-relaxed text-gray-600">
                      {template.description}
                    </p>

                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(template.category)}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {categories.find(c => c.id === template.category)?.name ||
                          template.category}
                      </span>
                      {template.createdAt && (
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {template.createdAt?.toDate
                              ? new Date(template.createdAt.toDate()).toLocaleDateString()
                              : template.createdAt instanceof Date
                                ? template.createdAt.toLocaleDateString()
                                : 'Recently'}
                          </span>
                        </div>
                      )}
                    </div>

                    {template.tags && template.tags.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-1.5">
                        {template.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                          >
                            {tag}
                          </span>
                        ))}
                        {template.tags.length > 3 && (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                            +{template.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}

                    {template.createdBy && (
                      <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                        <UserIcon className="h-3 w-3" />
                        <span>
                          By{' '}
                          {template.createdByFirstName || template.createdByLastName
                            ? `${template.createdByFirstName || ''} ${template.createdByLastName || ''}`.trim()
                            : template.createdByName || ''}{' '}
                          ({template.createdBy})
                        </span>
                      </div>
                    )}

                    {/* Analytics Badge */}
                    {(template.views > 0 || template.clicks > 0) && (
                      <div className="mb-4 flex items-center gap-3 text-xs text-gray-500">
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>{template.views || 0} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ChartBarIcon className="h-3 w-3" />
                          <span>{template.clicks || 0} clicks</span>
                        </div>
                      </div>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                      {template.documents && template.documents.length > 0 && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <DocumentArrowDownIcon className="h-4 w-4 text-amber-600" />
                          <span className="text-xs font-medium text-amber-700">
                            {template.documents.length} document
                            {template.documents.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                      {template.fileUrl && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <DocumentTextIcon className="h-4 w-4 text-amber-600" />
                          <span className="text-xs font-medium text-amber-700">File available</span>
                        </div>
                      )}
                      {template.link && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                          <LinkIcon className="h-4 w-4 text-amber-600" />
                          <span className="text-xs font-medium text-amber-700">External link</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Title
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Category
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Author
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Type
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Analytics
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                    Date
                  </th>
                  {isCoach && (
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-gray-700">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                <AnimatePresence>
                  {filteredTemplates.map((template, index) => {
                    const FileIcon = getFileIcon(template.fileType);
                    return (
                      <motion.tr
                        key={template.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ delay: index * 0.02 }}
                        onClick={() => handleCardClick(template)}
                        className="cursor-pointer transition-colors hover:bg-gray-50"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500 to-orange-600">
                              <FileIcon className="h-5 w-5 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold text-gray-900">
                                {template.title}
                              </div>
                              {template.description && (
                                <button
                                  onClick={e => {
                                    e.stopPropagation();
                                    handleCardClick(template);
                                  }}
                                  className="mt-1 text-xs font-medium text-amber-600 hover:text-amber-700 hover:underline"
                                >
                                  See more
                                </button>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(template.category)}`}
                          >
                            <TagIcon className="h-3 w-3" />
                            {categories.find(c => c.id === template.category)?.name ||
                              template.category}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          {template.createdBy && (
                            <div className="flex items-center gap-2">
                              <UserIcon className="h-4 w-4 text-gray-400" />
                              <span>
                                {template.createdByFirstName || template.createdByLastName
                                  ? `${template.createdByFirstName || ''} ${template.createdByLastName || ''}`.trim()
                                  : template.createdByName || ''}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-2">
                            {template.documents && template.documents.length > 0 && (
                              <span className="flex items-center gap-1 text-xs text-amber-600">
                                <DocumentArrowDownIcon className="h-3 w-3" />
                                {template.documents.length} doc
                                {template.documents.length > 1 ? 's' : ''}
                              </span>
                            )}
                            {template.fileUrl && (
                              <span className="flex items-center gap-1 text-xs text-amber-600">
                                <DocumentTextIcon className="h-3 w-3" />
                                File
                              </span>
                            )}
                            {template.link && (
                              <span className="flex items-center gap-1 text-xs text-amber-600">
                                <LinkIcon className="h-3 w-3" />
                                Link
                              </span>
                            )}
                            {!template.documents?.length && !template.fileUrl && !template.link && (
                              <span className="text-xs text-gray-400">Basic</span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-700">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <EyeIcon className="h-4 w-4 text-gray-400" />
                              <span>{template.views || 0}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <ChartBarIcon className="h-4 w-4 text-gray-400" />
                              <span>{template.clicks || 0}</span>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                          {template.createdAt?.toDate
                            ? new Date(template.createdAt.toDate()).toLocaleDateString()
                            : template.createdAt instanceof Date
                              ? template.createdAt.toLocaleDateString()
                              : 'Recently'}
                        </td>
                        {isCoach && (
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleEdit(template);
                                }}
                                className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50"
                                aria-label="Edit template"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </motion.button>
                              <motion.button
                                whileHover={{ scale: 1.1 }}
                                whileTap={{ scale: 0.9 }}
                                onClick={e => {
                                  e.stopPropagation();
                                  handleDeleteClick(template);
                                }}
                                className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50"
                                aria-label="Delete template"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </motion.button>
                            </div>
                          </td>
                        )}
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filteredTemplates.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="py-16 text-center"
        >
          <FolderIcon className="mx-auto mb-4 h-12 w-12 text-gray-400" />
          <h3 className="mb-2 text-lg font-semibold text-gray-700">
            {searchTerm || selectedCategory !== 'all'
              ? 'No templates match your filters'
              : 'No templates yet'}
          </h3>
          <p className="text-sm text-gray-500">
            {searchTerm || selectedCategory !== 'all'
              ? 'Try adjusting your search or filters'
              : isCoach
                ? 'Create your first template to get started!'
                : 'Check back later for templates'}
          </p>
        </motion.div>
      )}

      {/* Template Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => {
              setShowDetailModal(false);
              setSelectedTemplate(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={e => e.stopPropagation()}
              className="relative max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="border-b border-gray-200 bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-5">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium ${getCategoryColor(selectedTemplate.category)}`}
                      >
                        <TagIcon className="h-3 w-3" />
                        {categories.find(c => c.id === selectedTemplate.category)?.name ||
                          selectedTemplate.category}
                      </span>
                      {selectedTemplate.createdAt && (
                        <div className="flex items-center gap-1 text-xs text-white/80">
                          <ClockIcon className="h-3 w-3" />
                          <span>
                            {selectedTemplate.createdAt?.toDate
                              ? new Date(selectedTemplate.createdAt.toDate()).toLocaleDateString()
                              : selectedTemplate.createdAt instanceof Date
                                ? selectedTemplate.createdAt.toLocaleDateString()
                                : 'Recently'}
                          </span>
                        </div>
                      )}
                    </div>
                    <h2 className="text-2xl font-bold text-white">{selectedTemplate.title}</h2>
                    {selectedTemplate.createdBy && (
                      <p className="mt-2 text-sm text-white/90">
                        By{' '}
                        {selectedTemplate.createdByFirstName || selectedTemplate.createdByLastName
                          ? `${selectedTemplate.createdByFirstName || ''} ${selectedTemplate.createdByLastName || ''}`.trim()
                          : selectedTemplate.createdByName || ''}{' '}
                        ({selectedTemplate.createdBy})
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      setSelectedTemplate(null);
                    }}
                    className="rounded-lg p-2 text-white/90 transition-colors hover:bg-white/20"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="max-h-[calc(90vh-200px)] overflow-y-auto px-6 py-6">
                {/* Description */}
                <div className="mb-6">
                  <h3 className="mb-2 text-lg font-semibold text-gray-900">Description</h3>
                  <p className="leading-relaxed text-gray-700">{selectedTemplate.description}</p>
                </div>

                {/* Main File */}
                {selectedTemplate.fileUrl && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                      Template File
                    </h3>
                    <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                          {React.createElement(getFileIcon(selectedTemplate.fileType), {
                            className: 'h-5 w-5 text-amber-600',
                          })}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {selectedTemplate.fileName || selectedTemplate.title}
                          </p>
                          {selectedTemplate.fileType && (
                            <p className="text-xs text-gray-500">
                              {selectedTemplate.fileType.toUpperCase()} file
                            </p>
                          )}
                        </div>
                      </div>
                      <motion.a
                        href={selectedTemplate.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          // Track download analytics
                          const templateRef = doc(firestore, 'templates', selectedTemplate.id);
                          updateDoc(templateRef, {
                            clicks: increment(1),
                          });
                          addDoc(collection(firestore, 'templatesAnalytics'), {
                            templateId: selectedTemplate.id,
                            templateTitle: selectedTemplate.title,
                            userId: currentUser?.uid || 'anonymous',
                            userEmail: currentUser?.email || 'anonymous',
                            userRole: userRole || 'unknown',
                            action: 'file_download',
                            timestamp: serverTimestamp(),
                          }).catch(console.error);
                        }}
                        className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4" />
                        Download
                      </motion.a>
                    </div>
                  </div>
                )}

                {/* Documents */}
                {selectedTemplate.documents && selectedTemplate.documents.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <DocumentArrowDownIcon className="h-5 w-5 text-amber-600" />
                      Documents ({selectedTemplate.documents.length})
                    </h3>
                    <div className="space-y-2">
                      {selectedTemplate.documents.map((doc, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                              <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{doc.name}</p>
                              {doc.size && (
                                <p className="text-xs text-gray-500">
                                  {(doc.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                          </div>
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => {
                              // Track document download analytics
                              const templateRef = doc(firestore, 'templates', selectedTemplate.id);
                              updateDoc(templateRef, {
                                clicks: increment(1),
                              });
                              addDoc(collection(firestore, 'templatesAnalytics'), {
                                templateId: selectedTemplate.id,
                                templateTitle: selectedTemplate.title,
                                userId: currentUser?.uid || 'anonymous',
                                userEmail: currentUser?.email || 'anonymous',
                                userRole: userRole || 'unknown',
                                action: 'document_download',
                                documentName: doc.name,
                                timestamp: serverTimestamp(),
                              }).catch(console.error);
                            }}
                            className="flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            Download
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Steps */}
                {selectedTemplate.steps && selectedTemplate.steps.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <CheckCircleIcon className="h-5 w-5 text-amber-600" />
                      Steps ({selectedTemplate.steps.length})
                    </h3>
                    <div className="space-y-3">
                      {selectedTemplate.steps.map((step, idx) => {
                        const normalizedStep = normalizeStep(step);
                        return (
                          <div
                            key={idx}
                            className="flex items-start gap-4 rounded-lg border border-amber-100 bg-amber-50/50 p-4"
                          >
                            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-600 text-sm font-bold text-white">
                              {idx + 1}
                            </div>
                            <div className="flex-1">
                              <h4 className="mb-1 font-bold text-gray-900">
                                {normalizedStep.title}
                              </h4>
                              {normalizedStep.description && (
                                <p className="leading-relaxed text-gray-700">
                                  {normalizedStep.description}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Link */}
                {selectedTemplate.link && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <LinkIcon className="h-5 w-5 text-amber-600" />
                      External Link
                    </h3>
                    <a
                      href={selectedTemplate.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
                      onClick={() => {
                        // Track link click analytics
                        const templateRef = doc(firestore, 'templates', selectedTemplate.id);
                        updateDoc(templateRef, {
                          clicks: increment(1),
                        });
                        addDoc(collection(firestore, 'templatesAnalytics'), {
                          templateId: selectedTemplate.id,
                          templateTitle: selectedTemplate.title,
                          userId: currentUser?.uid || 'anonymous',
                          userEmail: currentUser?.email || 'anonymous',
                          userRole: userRole || 'unknown',
                          action: 'link_click',
                          timestamp: serverTimestamp(),
                        }).catch(console.error);
                      }}
                    >
                      <LinkIcon className="h-4 w-4" />
                      Access Link
                    </a>
                  </div>
                )}

                {/* Strategic Implications */}
                {selectedTemplate.strategicImplications && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <AcademicCapIcon className="h-5 w-5 text-amber-600" />
                      Strategic Implications
                    </h3>
                    <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-4">
                      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                        {selectedTemplate.strategicImplications}
                      </p>
                    </div>
                  </div>
                )}

                {/* Conclusion */}
                {selectedTemplate.conclusion && (
                  <div className="mb-6">
                    <h3 className="mb-3 flex items-center gap-2 text-lg font-semibold text-gray-900">
                      <CheckCircleIcon className="h-5 w-5 text-amber-600" />
                      Conclusion
                    </h3>
                    <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-4">
                      <p className="whitespace-pre-wrap leading-relaxed text-gray-700">
                        {selectedTemplate.conclusion}
                      </p>
                    </div>
                  </div>
                )}

                {/* Tags */}
                {selectedTemplate.tags && selectedTemplate.tags.length > 0 && (
                  <div className="mb-6">
                    <h3 className="mb-3 text-lg font-semibold text-gray-900">Tags</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedTemplate.tags.map((tag, idx) => (
                        <span
                          key={idx}
                          className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Analytics */}
                {(selectedTemplate.views > 0 || selectedTemplate.clicks > 0) && (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <ChartBarIcon className="h-4 w-4" />
                      Analytics
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Views</p>
                        <p className="text-2xl font-bold text-amber-600">
                          {selectedTemplate.views || 0}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Clicks</p>
                        <p className="text-2xl font-bold text-orange-600">
                          {selectedTemplate.clicks || 0}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              {isCoach && (
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-end gap-3">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDetailModal(false);
                        handleEdit(selectedTemplate);
                      }}
                      className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
                    >
                      <PencilIcon className="h-4 w-4" />
                      Edit
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setShowDetailModal(false);
                        handleDeleteClick(selectedTemplate);
                      }}
                      className="flex items-center gap-2 rounded-lg border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition-colors hover:bg-red-50"
                    >
                      <TrashIcon className="h-4 w-4" />
                      Delete
                    </motion.button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add/Edit Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
            onClick={() => {
              setShowModal(false);
              setEditingTemplate(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 mb-6 flex items-center justify-between border-b bg-white pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg">
                    <DocumentTextIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingTemplate ? 'Edit Template' : 'Create Template'}
                    </h3>
                    <p className="text-sm text-gray-500">Add a new template to the library</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingTemplate(null);
                    setDocumentFiles([]);
                  }}
                  className="rounded-xl p-2 transition-colors hover:bg-gray-100"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newTemplate.title}
                    onChange={e => setNewTemplate({ ...newTemplate, title: e.target.value })}
                    placeholder="Enter template title"
                    className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                    required
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={newTemplate.description}
                    onChange={e => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    rows={3}
                    placeholder="Describe the template..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      Category
                    </label>
                    <select
                      value={newTemplate.category}
                      onChange={e => setNewTemplate({ ...newTemplate, category: e.target.value })}
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                    >
                      {categories
                        .filter(c => c.id !== 'all')
                        .map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-semibold text-gray-700">
                      File Type (Optional)
                    </label>
                    <input
                      type="text"
                      value={newTemplate.fileType}
                      onChange={e => setNewTemplate({ ...newTemplate, fileType: e.target.value })}
                      placeholder="PDF, DOCX, XLSX, etc."
                      className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                    />
                  </div>
                </div>

                {/* File Upload (Legacy - Optional) */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Upload Template File (Optional)
                  </label>
                  <div className="space-y-3">
                    <div className="relative">
                      <input
                        type="file"
                        onChange={async e => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const maxSize = 50 * 1024 * 1024;
                          if (file.size > maxSize) {
                            toast.error('File size exceeds 50MB limit', 'Error');
                            return;
                          }
                          try {
                            const timestamp = Date.now();
                            const safeName = file.name.replace(/[^a-zA-Z0-9_.-]/g, '_');
                            const storagePath = `templates/${currentUser.uid}/${timestamp}_${safeName}`;
                            const storageRef = ref(storage, storagePath);
                            await uploadBytes(storageRef, file);
                            const downloadURL = await getDownloadURL(storageRef);
                            const fileExtension = file.name.split('.').pop().toLowerCase();
                            setNewTemplate({
                              ...newTemplate,
                              fileUrl: downloadURL,
                              fileName: file.name,
                              fileType: fileExtension,
                            });
                            toast.success('File uploaded successfully!');
                          } catch (error) {
                            console.error('Error uploading file:', error);
                            toast.error('Failed to upload file', 'Error');
                          }
                        }}
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        className="hidden"
                        id="template-file-upload"
                      />
                      <label
                        htmlFor="template-file-upload"
                        className={`flex cursor-pointer items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 transition-all ${
                          newTemplate.fileUrl
                            ? 'border-green-300 bg-green-50 hover:border-green-400'
                            : 'border-gray-300 bg-gray-50 hover:border-amber-400 hover:bg-amber-50/30'
                        }`}
                      >
                        <DocumentArrowUpIcon className="h-6 w-6 text-gray-400" />
                        <div className="text-center">
                          {newTemplate.fileUrl ? (
                            <>
                              <p className="text-sm font-medium text-green-700">
                                {newTemplate.fileName || 'File uploaded'}
                              </p>
                              <p className="text-xs text-green-600">Click to change file</p>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-700">
                                Click to upload or drag and drop
                              </p>
                              <p className="text-xs text-gray-500">
                                PDF, DOC, XLS, PPT, TXT (Max 50MB)
                              </p>
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Multiple Documents Upload */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Documents (Optional)
                  </label>
                  <div className="space-y-3">
                    {/* Existing Documents */}
                    {newTemplate.documents && newTemplate.documents.length > 0 && (
                      <div className="space-y-2">
                        {newTemplate.documents.map((doc, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                                <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                                {doc.size && (
                                  <p className="text-xs text-gray-500">
                                    {(doc.size / (1024 * 1024)).toFixed(2)} MB
                                  </p>
                                )}
                              </div>
                            </div>
                            {isCoach && editingTemplate && (
                              <button
                                type="button"
                                onClick={() => removeDocument(editingTemplate.id, idx)}
                                className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* New Files to Upload */}
                    {documentFiles.length > 0 && (
                      <div className="space-y-2">
                        {documentFiles.map((file, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 p-3"
                          >
                            <div className="flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                                <DocumentTextIcon className="h-5 w-5 text-amber-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                <p className="text-xs text-gray-500">
                                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDocumentFile(idx)}
                              className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Upload Button */}
                    <div className="flex items-center gap-3">
                      <label
                        htmlFor="document-upload"
                        className="flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-amber-500 hover:bg-amber-50 hover:text-amber-600"
                      >
                        <DocumentArrowDownIcon className="h-5 w-5" />
                        <span>
                          {documentFiles.length > 0 ? 'Add More Documents' : 'Upload Documents'}
                        </span>
                      </label>
                      <input
                        id="document-upload"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                        onChange={handleDocumentUpload}
                        className="hidden"
                      />
                      {uploadingDocuments && (
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-amber-200 border-t-amber-600"></div>
                          <span>Uploading...</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Supported formats: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV. Max size:
                      50MB per file. Multiple files allowed.
                    </p>
                  </div>
                </div>

                {/* External Link Option */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Or External Link (Alternative)
                  </label>
                  <input
                    type="url"
                    value={newTemplate.link}
                    onChange={e => setNewTemplate({ ...newTemplate, link: e.target.value })}
                    placeholder="https://... (optional external link)"
                    className="w-full rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Use this if you prefer to link to an external resource instead of uploading
                  </p>
                </div>

                {/* Steps */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Steps (Optional)
                  </label>
                  <div className="space-y-4">
                    {(newTemplate.steps || []).map((step, index) => {
                      const normalizedStep = normalizeStep(step);
                      return (
                        <div
                          key={index}
                          className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                        >
                          <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-amber-100 text-sm font-bold text-amber-600">
                                {index + 1}
                              </span>
                              <span className="text-sm font-semibold text-gray-700">
                                Step {index + 1}
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeStep(index)}
                              className="rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </div>
                          <div className="space-y-2">
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-700">
                                Step Title <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={normalizedStep.title}
                                onChange={e => updateStep(index, 'title', e.target.value)}
                                placeholder={`e.g., Identify the problem`}
                                className="w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                required
                              />
                            </div>
                            <div>
                              <label className="mb-1 block text-xs font-semibold text-gray-700">
                                Description
                              </label>
                              <textarea
                                value={normalizedStep.description}
                                onChange={e => updateStep(index, 'description', e.target.value)}
                                placeholder={`Describe what to do in this step...`}
                                rows={2}
                                className="w-full resize-none rounded-lg border border-gray-300 bg-white p-2.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={addStep}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-700 transition-all hover:border-amber-500 hover:bg-amber-50 hover:text-amber-600"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Add Step
                    </button>
                  </div>
                </div>

                {/* Strategic Implications */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Strategic Implications (Optional)
                  </label>
                  <textarea
                    value={newTemplate.strategicImplications}
                    onChange={e =>
                      setNewTemplate({ ...newTemplate, strategicImplications: e.target.value })
                    }
                    rows={6}
                    placeholder="Describe the strategic implications of this template..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>

                {/* Conclusion */}
                <div>
                  <label className="mb-2 block text-sm font-semibold text-gray-700">
                    Conclusion (Optional)
                  </label>
                  <textarea
                    value={newTemplate.conclusion}
                    onChange={e => setNewTemplate({ ...newTemplate, conclusion: e.target.value })}
                    rows={6}
                    placeholder="Write a conclusion summarizing key points..."
                    className="w-full resize-none rounded-xl border border-gray-300 bg-white p-3.5 text-sm transition-all focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                  />
                </div>

                <div className="sticky bottom-0 flex flex-col gap-3 border-t bg-white pb-2 pt-4 sm:flex-row">
                  <motion.button
                    type="button"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      setShowModal(false);
                      setEditingTemplate(null);
                      setDocumentFiles([]);
                    }}
                    disabled={uploadingDocuments || savingTemplate}
                    className="flex-1 rounded-xl border-2 border-gray-300 bg-white px-6 py-3.5 text-sm font-semibold text-gray-700 transition-all hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={uploadingDocuments || savingTemplate}
                    className="flex-1 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600 px-6 py-3.5 text-sm font-semibold text-white shadow-lg transition-all hover:from-amber-600 hover:to-orange-700 hover:shadow-xl disabled:opacity-50"
                  >
                    {uploadingDocuments || savingTemplate ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/20 border-t-white"></div>
                        {uploadingDocuments
                          ? 'Uploading documents...'
                          : savingTemplate
                            ? 'Saving...'
                            : 'Processing...'}
                      </span>
                    ) : editingTemplate ? (
                      'Update Template'
                    ) : (
                      'Create Template'
                    )}
                  </motion.button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmation
        open={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Template"
        message="Are you sure you want to delete this template? This action cannot be undone."
        itemName={templateToDelete?.title}
      />
    </motion.div>
  );
};

export default Templates;
